/**
 * iter233 · PLANNING WORKSPACE — the live factory planning sheet.
 *
 * Structure mirrors the user's MASTER PP workbook (`PRODUCTION PLANING`
 * sheet) but connected to live ERP records:
 *
 *   Day  →  Customer / purpose  →  production slots
 *   (each day also lists ⚠ NEEDS PLANNING requirements with no slot yet)
 *
 * Always opens on the current planning period (rolling 7 days from the
 * factory's today) with a pinned OVERDUE / CARRY FORWARD group. Nothing is
 * ever deleted — History and Cancelled are separate buckets.
 *
 * Backend: GET /api/factory-plans/views/planning-workspace
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, RotateCw, ChevronDown, ChevronRight, AlertTriangle,
  Plus, Columns3, Archive, RotateCcw,
} from 'lucide-react';
import { fmtQty } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import {
  COLUMNS, COMPACT_KEYS, PERIODS, API_FIELD, canPlan, isFactoryHead,
} from './planningColumns';
import PlanningSheetRow from './PlanningSheetRow';
import PlanningWatchPanel from './PlanningWatchPanel';
import PlanSlotModal from './PlanSlotModal';
import PlanNotesDrawer from './PlanNotesDrawer';
import RmDrawer from './RmDrawer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LS_COLS = 'ti_pw_cols_v1';

const BUCKETS = [
  { v: 'active', l: 'Active' },
  { v: 'overdue', l: '⚠ Overdue' },
  { v: 'history', l: 'History' },
  { v: 'cancelled', l: 'Cancelled' },
];

// spec 28 — hand the question to the MAIN Hey TI assistant, never a mini chat.
const askHeyTi = (q) => {
  try { window.dispatchEvent(new CustomEvent('heyti:open', { detail: { message: q } })); }
  catch { /* no-op */ }
};

export default function PlanningWorkspace() {
  const { user } = useAuth();
  const role = user?.role;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState('active');
  const [preset, setPreset] = useState('rolling7');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [q, setQ] = useState('');
  // The pinned carry-forward group starts collapsed so today's plan is
  // the first thing the Factory Head sees.
  const [collapsedDays, setCollapsedDays] = useState({ OVERDUE: true });
  const [collapsedCust, setCollapsedCust] = useState({});
  const [expanded, setExpanded] = useState({});
  const [showCols, setShowCols] = useState(false);
  const [visible, setVisible] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_COLS) || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {/* ignore */}
    return COMPACT_KEYS;
  });
  const [modal, setModal] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const [closing, setClosing] = useState(false);
  const [rmFor, setRmFor] = useState(null);
  const [notesFor, setNotesFor] = useState(null);

  const cols = useMemo(() => COLUMNS.filter(c => visible.includes(c.k)), [visible]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/factory-plans/views/planning-workspace`, {
        params: {
          bucket, preset, q: q || undefined,
          date_from: preset === 'custom' ? custom.from || undefined : undefined,
          date_to: preset === 'custom' ? custom.to || undefined : undefined,
        },
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load the planning sheet');
    } finally { setLoading(false); }
  }, [bucket, preset, q, custom.from, custom.to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleCol = (k) => setVisible(v => {
    const next = v.includes(k) ? v.filter(x => x !== k) : [...v, k];
    localStorage.setItem(LS_COLS, JSON.stringify(next));
    return next;
  });

  const findRow = (planId) => {
    for (const day of data?.days || []) {
      for (const c of day.customers) {
        const hit = c.rows.find(r => r.id === planId);
        if (hit) return hit;
      }
    }
    return null;
  };

  const reserveFor = async (planId) => {
    try {
      const res = await axios.post(`${API}/rm-reservations/reserve/${planId}`, {}, { withCredentials: true });
      toast[res.data.at_risk ? 'warning' : 'success'](res.data.at_risk
        ? 'Reserved what exists · slot tagged ⚠ AT RISK'
        : `Reserved ${res.data.reservations.length} material(s)`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reserve failed'); }
  };

  const buyFor = async (planId) => {
    try {
      const res = await axios.post(`${API}/factory-plans/${planId}/purchase-requirement`, {}, { withCredentials: true });
      res.data.success ? toast.success(res.data.message) : toast.info(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not raise the purchase'); }
  };

  const moveTo = (planId, date) => {
    const row = findRow(planId);
    if (row) setModal({ mode: 'move', seed: { ...row, planned_date: date || row.planned_date } });
  };

  const planFromOrder = (order) => {
    const first = order.needs_planning?.[0];
    if (!first) return;
    setModal({
      mode: 'from-requirement',
      seed: {
        product_name: first.product_name, quantity: first.qty,
        customer_name: order.customer_name, customer_id: order.customer_id || '',
        sales_order_id: order.sales_order_id, sales_order_number: order.sales_order_number,
        required_date: order.required_date, planned_date: data?.today,
        purpose: order.customer_name ? 'customer_order' : 'stock_replenishment',
      },
    });
  };

  const reopen = async (row) => {
    try {
      await axios.post(`${API}/factory-plans/reopen/${row.id}`, {}, { withCredentials: true });
      toast.success('Slot reopened into active planning');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reopen failed'); }
  };

  const closeOut = async () => {
    const n = data?.buckets?.overdue || 0;
    if (!window.confirm(`Close out ${n} unfinished plan(s) dated before today, and take stale order requirements off the planning sheet?\n\nNothing is deleted — closed plans move to History, sales orders and invoices are untouched, and any reserved raw material goes back to stock.`)) return;
    setClosing(true);
    try {
      const res = await axios.post(`${API}/factory-plans/close-out`, { scope: 'both' }, { withCredentials: true });
      toast.success(`${res.data.plans_closed} plan(s) closed · ${res.data.order_requirements_closed} requirement(s) archived · ${res.data.reservations_released} RM reservation(s) released`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Close-out failed'); }
    finally { setClosing(false); }
  };

  const patchCell = async (row, uiField, value) => {
    const field = API_FIELD[uiField] || uiField;
    try {
      await axios.patch(`${API}/factory-plans/${row.id}`, { [field]: value }, { withCredentials: true });
      toast.success(`${uiField.replace(/_/g, ' ')} updated`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const rowAction = async (row, kind) => {
    if (kind === 'notes') return setNotesFor(row);
    if (kind === 'rm') return setRmFor(row);
    if (kind === 'edit') return setModal({ mode: 'edit', seed: row });
    if (kind === 'duplicate') return setModal({ mode: 'duplicate', seed: row });
    if (kind === 'move') return setModal({ mode: 'move', seed: row });
    if (kind === 'cancel') {
      if (!window.confirm(`Cancel ${row.id} — ${row.product_name} ${fmtQty(row.total_planned, 'kg')}?\n\nThe slot is kept for audit and any reserved raw material goes back to stock.`)) return;
      try {
        await axios.delete(`${API}/factory-plans/${row.id}`, { withCredentials: true });
        toast.success('Slot cancelled · RM released');
        fetchData();
      } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
    }
  };

  const s = data?.summary;
  const cards = s ? [
    { l: '🔴 Left to Make', v: fmtQty(s.left, 'kg'), tone: 'text-red-400' },
    { l: '🟢 Made / Available', v: fmtQty(s.made, 'kg'), tone: 'text-emerald-400' },
    { l: '🔵 Dispatched', v: fmtQty(s.dispatched, 'kg'), tone: 'text-blue-400' },
    { l: 'Planned', v: fmtQty(s.planned, 'kg'), tone: 'text-zinc-100' },
    { l: 'Batches', v: s.batches, tone: 'text-zinc-100' },
    { l: 'Needs Planning', v: s.needs_planning_count, tone: s.needs_planning_count ? 'text-orange-400' : 'text-zinc-400' },
    { l: 'RM Short', v: s.rm_short + s.rm_partial, tone: (s.rm_short + s.rm_partial) ? 'text-red-400' : 'text-zinc-400' },
    { l: 'Rule Warnings', v: s.rule_warnings ?? 0, tone: s.rule_warnings ? 'text-red-400' : 'text-emerald-400' },
  ] : [];

  return (
    <div className="space-y-3" data-testid="fp-planning-workspace">
      {/* Period + bucket bar */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-2.5 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-zinc-800/70 rounded-lg p-0.5">
          {BUCKETS.map(b => (
            <button key={b.v} onClick={() => { setBucket(b.v); if (b.v === 'history' || b.v === 'cancelled') setPreset('this_month'); else setPreset('rolling7'); }}
              data-testid={`fp-pw-bucket-${b.v}`}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${bucket === b.v ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-100'}`}>
              {b.l}{data?.buckets ? ` (${data.buckets[b.v === 'overdue' ? 'overdue' : b.v] ?? 0})` : ''}
            </button>
          ))}
        </div>

        <select value={preset} onChange={e => setPreset(e.target.value)} data-testid="fp-pw-preset"
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-[11px] text-zinc-100 focus:outline-none focus:border-amber-500">
          {PERIODS.map(p => <option key={p.v} value={p.v} className="bg-zinc-900">{p.l}</option>)}
        </select>
        {preset === 'custom' && (
          <>
            <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))}
              data-testid="fp-pw-from" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-[11px] text-zinc-100" />
            <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))}
              data-testid="fp-pw-to" className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-[11px] text-zinc-100" />
          </>
        )}

        <span className="text-[11px] text-amber-300 font-semibold" data-testid="fp-pw-period-label">
          {data?.period?.label || '—'}
        </span>
        <span className="text-[10px] text-zinc-500">Today · {data?.today_label || '—'}</span>

        <div className="flex items-center gap-1.5 flex-1 min-w-[180px] bg-zinc-800 rounded-lg px-2.5 py-1.5">
          <Search size={12} className="text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="fp-pw-search"
            placeholder="Search product / customer / SO / line / remark…"
            className="w-full bg-transparent text-[11px] text-zinc-100 focus:outline-none" />
        </div>

        <button onClick={() => setShowCols(x => !x)} data-testid="fp-pw-columns-toggle" title="Columns"
          className="ti-btn-ghost text-[11px] px-2 py-1.5 rounded-lg flex items-center gap-1">
          <Columns3 size={12} /> Columns
        </button>
        <button onClick={fetchData} title="Refresh" data-testid="fp-pw-refresh" className="text-zinc-500 hover:text-zinc-200 p-1.5">
          <RotateCw size={13} />
        </button>
        {isFactoryHead(role) && (data?.buckets?.overdue || 0) > 0 && (
          <button onClick={closeOut} disabled={closing} data-testid="fp-pw-close-out"
            title="Move unfinished older plans to History and take stale requirements off the sheet"
            className="ti-btn-ghost text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1 border-red-500/30 text-red-300 hover:text-red-200">
            <Archive size={12} /> {closing ? 'Closing…' : `Close out ${data.buckets.overdue} overdue`}
          </button>
        )}
        {canPlan(role) && (
          <button onClick={() => setModal({ mode: 'new', seed: { planned_date: data?.today } })}
            data-testid="fp-pw-new-slot"
            className="bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1">
            <Plus size={12} /> New slot
          </button>
        )}
      </div>

      {showCols && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 flex flex-wrap gap-2" data-testid="fp-pw-columns-panel">
          {COLUMNS.filter(c => c.k !== 'actions').map(c => (
            <button key={c.k} onClick={() => toggleCol(c.k)}
              data-testid={`fp-pw-col-${c.k}`}
              className={`text-[10px] px-2 py-1 rounded-full border ${visible.includes(c.k)
                ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                : 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}>
              {c.l}
            </button>
          ))}
          <button onClick={() => { setVisible(COLUMNS.map(c => c.k)); localStorage.setItem(LS_COLS, JSON.stringify(COLUMNS.map(c => c.k))); }}
            className="text-[10px] px-2 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">Full sheet</button>
          <button onClick={() => { setVisible(COMPACT_KEYS); localStorage.setItem(LS_COLS, JSON.stringify(COMPACT_KEYS)); }}
            className="text-[10px] px-2 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">Compact</button>
        </div>
      )}

      {/* Manufacturing rulebook — warn only (user choice), never blocks */}
      {(data?.rule_flags?.length > 0 || data?.rule_alerts?.length > 0) && (
        <div className="bg-red-500/[0.07] border border-red-500/30 rounded-xl p-3 space-y-1.5"
          data-testid="fp-pw-rule-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-[12px] font-semibold text-red-200">
              {(data.rule_flags || []).filter(f => f.level === 'warn').length} slot(s) break a manufacturing rule
            </span>
            <span className="flex-1" />
            <button onClick={() => askHeyTi('Check my production plan against my manufacturing rules and tell me exactly what to change.')}
              data-testid="fp-pw-rule-ask" className="text-[10px] text-amber-300 hover:text-amber-200 underline">
              ask Hey TI what to fix
            </button>
          </div>
          {(data.rule_flags || []).filter(f => f.level === 'warn').slice(0, 6).map((f, i) => (
            <p key={i} className="text-[11px] text-zinc-300" data-testid={`fp-pw-rule-flag-${i}`}>
              <span className="font-mono text-red-300">{f.plan_id}</span> · {f.text}
              {f.action && <span className="text-zinc-500"> → {f.action}</span>}
            </p>
          ))}
          {(data.rule_alerts || []).map((a, i) => (
            <p key={`a${i}`} className={`text-[11px] ${a.level === 'warn' ? 'text-orange-200' : 'text-zinc-400'}`}
              data-testid={`fp-pw-rule-alert-${i}`}>
              <span className={a.level === 'warn' ? 'text-orange-300' : 'text-zinc-500'}>{a.title}</span> · {a.text}
            </p>
          ))}
        </div>
      )}

      {/* Live intelligence — RM & order fulfilment watch (spec 29A) */}
      <PlanningWatchPanel data={data} onAsk={askHeyTi}
        onReserve={reserveFor} onBuy={buyFor} onMove={moveTo}
        onPlanRequirement={planFromOrder} />

      {/* Summary */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="fp-pw-summary">
          {cards.map((c, i) => (
            <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-2.5">
              <p className="text-[10px] uppercase text-zinc-500 tracking-wider">{c.l}</p>
              <p className={`text-base font-heading font-bold ${c.tone}`}>{c.v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Sheet */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-420px)]">
          <table className="text-[11px] w-full" data-testid="fp-pw-table">
            <thead className="sticky top-0 z-10 bg-zinc-900">
              <tr>
                <th className="px-2 py-2 sticky left-0 bg-zinc-900 z-20 border-b border-zinc-700" style={{ minWidth: 38 }} />
                <th className="px-2 py-2 sticky bg-zinc-900 z-20 border-b border-zinc-700 text-left text-[10px] uppercase tracking-wider text-zinc-400"
                  style={{ left: 38, minWidth: 190 }}>Product</th>
                {cols.map(c => (
                  <th key={c.k} style={{ minWidth: c.w }}
                    className={`px-2 py-2 border-b border-zinc-700 text-[10px] uppercase font-semibold tracking-wider whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.tone === 'red' ? 'text-red-400' : c.tone === 'green' ? 'text-emerald-400' : c.tone === 'blue' ? 'text-blue-400' : 'text-zinc-400'}`}>
                    {c.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cols.length + 2} className="text-center py-10 text-zinc-500">Loading the sheet…</td></tr>}
              {!loading && (data?.days || []).every(d => d.customers.length === 0 && d.needs_planning.length === 0) && (
                <tr><td colSpan={cols.length + 2} className="text-center py-10 text-zinc-600">
                  Nothing planned in {data?.period?.label || 'this period'}.
                  {canPlan(role) && ' Use “New slot” or plan a requirement from the Order Book.'}
                </td></tr>
              )}

              {!loading && (data?.days || []).map(day => {
                const empty = day.customers.length === 0 && day.needs_planning.length === 0;
                if (empty && bucket !== 'active') return null;
                const dayCollapsed = collapsedDays[day.date];
                return (
                  <React.Fragment key={day.date}>
                    <tr className={`cursor-pointer border-y ${day.is_overdue_group ? 'bg-red-500/10 border-red-500/25' : day.is_today ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-800/50 border-zinc-700/40'}`}
                      onClick={() => setCollapsedDays(x => ({ ...x, [day.date]: !x[day.date] }))}
                      data-testid={`fp-pw-day-${day.date}`}>
                      <td colSpan={cols.length + 2} className="px-3 py-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={day.is_overdue_group ? 'text-red-300' : 'text-amber-400'}>
                            {dayCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                          </span>
                          <span className="text-sm font-heading font-bold text-zinc-50">
                            {day.is_overdue_group ? day.day : `${day.day} · ${day.date}`}
                          </span>
                          {day.is_today && <span className="text-[10px] bg-amber-500 text-black px-1.5 py-0.5 rounded font-bold">TODAY</span>}
                          <span className="text-[10px] text-zinc-500">{day.totals.count} slot(s) · {day.totals.batches} batch(es)</span>
                          {day.needs_planning.length > 0 && (
                            <span className="text-[10px] text-orange-300">⚠ {day.needs_planning.length} needs planning</span>
                          )}
                          {canPlan(role) && !day.is_overdue_group && (
                            <button onClick={(e) => { e.stopPropagation(); setModal({ mode: 'new', seed: { planned_date: day.date } }); }}
                              data-testid={`fp-pw-day-add-${day.date}`}
                              className="text-[10px] text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                              <Plus size={10} /> slot
                            </button>
                          )}
                          <div className="flex-1" />
                          <span className="text-[11px] text-red-400">🔴 {fmtQty(day.totals.left, 'kg')}</span>
                          <span className="text-[11px] text-emerald-400">🟢 {fmtQty(day.totals.made, 'kg')}</span>
                          <span className="text-[11px] text-blue-400">🔵 {fmtQty(day.totals.dispatched, 'kg')}</span>
                          {day.totals.rm_short > 0 && <span className="text-[11px] text-red-400">🧪 {day.totals.rm_short} short</span>}
                        </div>
                      </td>
                    </tr>

                    {!dayCollapsed && day.needs_planning.map(req => (
                      <tr key={req.row_id} className="border-b border-zinc-800/60 bg-red-500/[0.06]"
                        data-testid={`fp-pw-need-${req.row_id}`}>
                        <td className="px-2 py-1 sticky left-0 bg-zinc-900 z-[1] border-r border-zinc-800/60">
                          <AlertTriangle size={12} className="text-red-400" />
                        </td>
                        <td className="px-2 py-1 sticky bg-zinc-900 z-[1]" style={{ left: 38 }}>
                          <span className="text-zinc-100">{req.product_name}</span>
                          <span className="block text-[10px] text-red-300">
                            NEEDS PLANNING · {req.customer_name}
                            {req.sales_order_number ? ` · SO ${req.sales_order_number}` : ''}
                          </span>
                        </td>
                        <td colSpan={cols.length} className="px-2 py-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-red-300 font-mono">🔴 {fmtQty(req.needs_qty, 'kg')} to plan</span>
                            <span className="text-zinc-500">ordered {fmtQty(req.required_quantity, 'kg')}</span>
                            {req.planned_open > 0.001 && <span className="text-zinc-500">already planned {fmtQty(req.planned_open, 'kg')}</span>}
                            <span className={req.is_overdue ? 'text-red-400 font-semibold' : 'text-zinc-400'}>
                              required {req.required_date || '—'}
                            </span>
                            {canPlan(role) && (
                              <button onClick={() => setModal({
                                mode: 'from-requirement',
                                seed: {
                                  ...req, quantity: req.needs_qty,
                                  planned_date: day.is_overdue_group ? data.today : day.date,
                                  purpose: req.customer_name ? 'customer_order' : 'stock_replenishment',
                                },
                              })} data-testid={`fp-pw-need-plan-${req.row_id}`}
                                className="bg-amber-500 hover:bg-amber-400 text-black text-[10px] font-semibold px-2.5 py-1 rounded-lg">
                                Plan it →
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {!dayCollapsed && day.customers.map(cust => {
                      const ck = `${day.date}::${cust.key}`;
                      const custCollapsed = collapsedCust[ck];
                      return (
                        <React.Fragment key={ck}>
                          <tr className="border-b border-zinc-800/60 bg-zinc-800/25 cursor-pointer"
                            onClick={() => setCollapsedCust(x => ({ ...x, [ck]: !x[ck] }))}
                            data-testid={`fp-pw-cust-${ck}`}>
                            <td className="px-2 py-1 sticky left-0 bg-zinc-900 z-[1] border-r border-zinc-800/60">
                              <span className="text-zinc-500">{custCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}</span>
                            </td>
                            <td colSpan={cols.length + 1} className="px-2 py-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-amber-200 font-semibold">{cust.customer_name}</span>
                                <span className="text-[10px] text-zinc-500">{cust.totals.count} product(s) · {fmtQty(cust.totals.planned, 'kg')} planned</span>
                                <div className="flex-1" />
                                <span className="text-[10px] text-red-400">🔴 {fmtQty(cust.totals.left, 'kg')}</span>
                                <span className="text-[10px] text-emerald-400">🟢 {fmtQty(cust.totals.made, 'kg')}</span>
                                <span className="text-[10px] text-blue-400">🔵 {fmtQty(cust.totals.dispatched, 'kg')}</span>
                              </div>
                            </td>
                          </tr>
                          {!custCollapsed && cust.rows.map(row => (
                            <PlanningSheetRow key={row.id} row={row} cols={cols} role={role}
                              isNew={justAdded === row.id}
                              expanded={!!expanded[row.id]}
                              onToggle={() => setExpanded(x => ({ ...x, [row.id]: !x[row.id] }))}
                              onPatch={(field, value) => patchCell(row, field, value)}
                              onAction={(kind) => (kind === 'reopen' ? reopen(row) : rowAction(row, kind))} />
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <PlanSlotModal mode={modal.mode} seed={modal.seed} role={role}
          onClose={() => setModal(null)}
          onSaved={(created) => {
            if (created?.id) {
              setJustAdded(created.id);
              setTimeout(() => setJustAdded(null), 8000);
            }
            fetchData();
          }} />
      )}
      {rmFor && (
        <RmDrawer planId={rmFor.id} title={rmFor.product_name}
          subtitle={rmFor.customer_name || rmFor.purpose_label}
          onClose={() => setRmFor(null)} onChanged={fetchData} />
      )}
      {notesFor && (
        <PlanNotesDrawer planId={notesFor.id} title={notesFor.product_name}
          subtitle={`${notesFor.planned_date} · ${notesFor.shift_label}`}
          onClose={() => setNotesFor(null)} onChanged={fetchData} />
      )}
    </div>
  );
}
